import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useAuth, useLoginWithRedirect } from "@frontegg/react";
import { useEffect, useRef, useState } from "react";
import { authTokenManager as defaultTokenManager } from "./authTokenManager";
import { decodeL500Token } from "./decodeL500Token";
const LOGIN_REDIRECT_DELAY_MS = 500;
function msUntilRefresh(l500Token, refreshLeadTimeMs, minRefreshDelayMs) {
    const decoded = decodeL500Token(l500Token);
    if (!decoded)
        return null;
    const expMs = decoded.exp * 1000;
    const lead = expMs - Date.now() - refreshLeadTimeMs;
    return Math.max(lead, minRefreshDelayMs);
}
function clearRefreshTimer(timerRef) {
    if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
    }
}
export function AuthenticatedAppWrapper({ tokenExchangeUrl, buildExchangeBody, children, tokenManager = defaultTokenManager, refreshLeadTimeMs = 60_000, minRefreshDelayMs = 1_000, renderLoading, renderError, onStateChange, }) {
    const { isAuthenticated, isLoading, user } = useAuth();
    const loginWithRedirect = useLoginWithRedirect();
    const loginInitiatedRef = useRef(false);
    const refreshTimerRef = useRef(null);
    // Tracks whether a token has ever been obtained on this mount. Once true, all
    // subsequent exchanges (the scheduled refresh, or a re-run after the Frontegg JWT
    // changes) run silently — keeping {children} mounted instead of blanking the app.
    const hasReadyTokenRef = useRef(false);
    const [tokenState, setTokenState] = useState("idle");
    const [tokenError, setTokenError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    function transitionTo(nextState) {
        setTokenState(nextState);
        onStateChange?.(nextState);
    }
    useEffect(() => {
        if (!user?.accessToken) {
            tokenManager.clearToken();
            hasReadyTokenRef.current = false;
            transitionTo("idle");
            clearRefreshTimer(refreshTimerRef);
            return;
        }
        const fronteggToken = user.accessToken;
        let cancelled = false;
        async function performExchange() {
            if (cancelled)
                return;
            // Only the very first exchange per mount gates the UI. Once we have a token,
            // refreshes are silent so the app subtree never unmounts.
            const background = hasReadyTokenRef.current;
            if (!background) {
                transitionTo("exchanging");
                setTokenError(null);
            }
            try {
                const response = await fetch(tokenExchangeUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(buildExchangeBody(fronteggToken)),
                });
                if (!response.ok) {
                    const body = await response.text();
                    throw new Error(`Token exchange failed: ${response.status} ${body}`);
                }
                const data = (await response.json());
                if (cancelled)
                    return;
                tokenManager.setToken(data.token);
                hasReadyTokenRef.current = true;
                // On a background refresh, keep tokenState at "ready" — no transition, no re-render gate.
                if (!background)
                    transitionTo("ready");
                scheduleRefresh(data.token);
            }
            catch (err) {
                if (cancelled)
                    return;
                if (background) {
                    // The current token is still valid until its exp; keep the app mounted and
                    // retry shortly rather than blanking the screen on a transient refresh failure.
                    scheduleRetry();
                    return;
                }
                hasReadyTokenRef.current = false;
                tokenManager.clearToken();
                setTokenError(err instanceof Error ? err : new Error("Token exchange failed"));
                transitionTo("error");
            }
        }
        function scheduleRefresh(l500Token) {
            clearRefreshTimer(refreshTimerRef);
            const delay = msUntilRefresh(l500Token, refreshLeadTimeMs, minRefreshDelayMs);
            if (delay === null)
                return;
            refreshTimerRef.current = window.setTimeout(() => {
                refreshTimerRef.current = null;
                void performExchange();
            }, delay);
        }
        function scheduleRetry() {
            clearRefreshTimer(refreshTimerRef);
            refreshTimerRef.current = window.setTimeout(() => {
                refreshTimerRef.current = null;
                void performExchange();
            }, minRefreshDelayMs);
        }
        void performExchange();
        return () => {
            cancelled = true;
            clearRefreshTimer(refreshTimerRef);
        };
    }, [user?.accessToken, retryCount]);
    useEffect(() => {
        if (isLoading || isAuthenticated || loginInitiatedRef.current) {
            return;
        }
        const timeoutId = window.setTimeout(() => {
            loginInitiatedRef.current = true;
            loginWithRedirect();
        }, LOGIN_REDIRECT_DELAY_MS);
        return () => window.clearTimeout(timeoutId);
    }, [isAuthenticated, isLoading, loginWithRedirect]);
    if (isLoading) {
        return renderLoading ? _jsx(_Fragment, { children: renderLoading() }) : null;
    }
    if (!isAuthenticated) {
        return renderLoading ? _jsx(_Fragment, { children: renderLoading() }) : null;
    }
    if (tokenState === "error") {
        if (renderError && tokenError) {
            return _jsx(_Fragment, { children: renderError(tokenError, () => setRetryCount(c => c + 1)) });
        }
        return null;
    }
    if (tokenState !== "ready") {
        return renderLoading ? _jsx(_Fragment, { children: renderLoading() }) : null;
    }
    return _jsx(_Fragment, { children: children });
}
//# sourceMappingURL=AuthenticatedAppWrapper.js.map